/**
 * GutachtenEditor.tsx — Rich-Text-Editor für Gutachtentexte
 * 
 * DESIGN: Nordic Command Center — Clean, professionell
 * 
 * Features:
 * - TipTap Rich-Text-Editor mit Toolbar
 * - Automatische Befüllung aus Akten-Daten (Fahrzeug, Halter, Schaden)
 * - Textbausteine einfügen per Dropdown/Suche
 * - Abschnitte: Auftrag, Fahrzeugbeschreibung, Besichtigung, Schadenbeschreibung, Bewertung, Ergebnis
 * - Vorschau des fertigen Dokuments mit Briefkopf
 * - Speichern in localStorage (später Supabase)
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Undo, Redo, FileText, Eye, Edit3, Save, BookOpen,
  ChevronDown, Search, Plus, Table as TableIcon, Type, Heading1, Heading2,
  Heading3, Minus, Printer, Download, Check, X, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Typen ────────────────────────────────────────────────────────────────────

/** Aktendaten die automatisch ins Gutachten eingefügt werden */
export interface AktenDaten {
  aktenNr: string;
  auftragsDatum: string;
  gutachtenTyp: string; // z.B. "Haftpflichtgutachten"
  halter: {
    anrede: string;
    vorname: string;
    nachname: string;
    strasse: string;
    plz: string;
    ort: string;
    land: string;
  };
  fahrzeug: {
    kennzeichen: string;
    fin: string;
    hsnTsn: string;
    hersteller: string;
    modell: string;
    typ: string;
    bauart: string;
    farbe: string;
    erstzulassung: string;
    laufleistung: string;
    naechsteHU: string;
    kraftstoff: string;
    leistungKw: string;
    hubraum: string;
    getriebe: string;
    antrieb: string;
  };
  besichtigung: {
    datum: string;
    uhrzeit: string;
    ort: string;
    firma: string;
    adresse: string;
    besichtigtDurch: string;
  };
  bewertung: {
    wiederbeschaffungswert: string;
    restwert: string;
    reparaturkosten: string;
    wertminderung: string;
    nutzungsausfall: string;
  };
  schadenBeschreibung: string;
}

export interface Textbaustein {
  id: string;
  kategorie: string;
  titel: string;
  text: string;
  platzhalter?: string[]; // z.B. ["{{DATUM}}", "{{HALTER}}"]
}

// ─── Standard-Textbausteine ──────────────────────────────────────────────────

const STANDARD_TEXTBAUSTEINE: Textbaustein[] = [
  // Auftrag
  {
    id: 'tb_auftrag_01',
    kategorie: 'Auftrag',
    titel: 'Standard-Auftragstext',
    text: 'Gemäß Auftrag vom {{AUFTRAGSDATUM}} durch {{AUFTRAGGEBER}} wurde das in den Fahrzeugdaten näher bezeichnete Fahrzeug zur Beweissicherung und Feststellung der Schadenhöhe besichtigt und darüber ein Gutachten erstellt.\n\nBestandteil des Gutachtens ist die Dokumentation des Schadens, sowie die Ermittlung der erforderlichen Kosten zur fachgerechten Instandsetzung in einer Fach- oder Markenvertragswerkstatt.\n\nIm Rahmen der Gutachtenerstellung kann es zur genauen Schadenfeststellung erforderlich sein Teilzerlegungen, Fahrwerksvermessungen, Rahmenvermessungen oder Fehlerspeicherauslesearbeiten durchzuführen. Die Kosten für etwaige notwendige Arbeiten sind nicht Bestandteil des Gutachtens.\n\nDie rechtliche Durchsetzung der Ansprüche aus dem Gutachten obliegt dem Anspruchsteller bzw. seinem Anwalt.',
  },
  // Besichtigung
  {
    id: 'tb_besichtigung_01',
    kategorie: 'Besichtigung',
    titel: 'Standard-Besichtigungstext',
    text: 'Die Besichtigungsbedingungen waren zur Schadenfeststellung ausreichend. Zur Untersuchung des Fahrzeugs standen Hubmittel bzw. Grube zur Verfügung. Das Fahrzeug konnte von unten besichtigt werden.',
  },
  {
    id: 'tb_besichtigung_02',
    kategorie: 'Besichtigung',
    titel: 'Hebebühne',
    text: 'Die Besichtigung der Fahrzeugunterseite erfolgte im angehobenen Zustand auf einer Hebebühne.',
  },
  {
    id: 'tb_besichtigung_03',
    kategorie: 'Besichtigung',
    titel: 'Hilfsmittel',
    text: 'Folgende Hilfsmittel wurden bei der Begutachtung verwendet:\n• Profiltiefenmesser\n• Lackschichtdickenmesser',
  },
  {
    id: 'tb_besichtigung_04',
    kategorie: 'Besichtigung',
    titel: 'Demontage',
    text: 'Zur genaueren Schadensfeststellung wurden beschädigte Fahrzeugteile demontiert. Die ausgebauten beschädigten Teile lagen vor.',
  },
  {
    id: 'tb_besichtigung_05',
    kategorie: 'Besichtigung',
    titel: 'Probelauf',
    text: 'Ein Probelauf des Fahrzeugmotors wurde durchgeführt.',
  },
  // Zustand
  {
    id: 'tb_zustand_01',
    kategorie: 'Zustand',
    titel: 'Gepflegt, normale Gebrauchsspuren',
    text: 'Der Allgemeinzustand des Fahrzeuges bezogen auf Alter und Nutzung war gepflegt, normale Gebrauchsspuren.\n\nDer Karosseriezustand des Fahrzeuges war gepflegt, normale Gebrauchsspuren.\n\nDer Lackzustand des Fahrzeuges war gepflegt, normale Gebrauchsspuren.\n\nDer Innenraumzustand des Fahrzeuges war gepflegt, normale Gebrauchsspuren.',
  },
  {
    id: 'tb_zustand_02',
    kategorie: 'Zustand',
    titel: 'Überdurchschnittlich gepflegt',
    text: 'Der Allgemeinzustand des Fahrzeuges bezogen auf Alter und Nutzung war überdurchschnittlich gepflegt.\n\nDer Karosseriezustand des Fahrzeuges war sehr gut.\n\nDer Lackzustand des Fahrzeuges war einwandfrei.\n\nDer Innenraumzustand des Fahrzeuges war sehr gepflegt.',
  },
  // Vorschäden
  {
    id: 'tb_vorschaeden_01',
    kategorie: 'Vorschäden',
    titel: 'Keine Vorschäden',
    text: 'Es wurden keine reparierten Vorschäden am Fahrzeug festgestellt.\n\nEs wurden keine nicht reparierten Vorschäden am Fahrzeug festgestellt.\n\nEs wurden keine unfachgemäß reparierten Vorschäden am Fahrzeug festgestellt.',
  },
  // Wertermittlung
  {
    id: 'tb_wert_01',
    kategorie: 'Wertermittlung',
    titel: 'Wiederbeschaffungswert Standard',
    text: 'Der Wiederbeschaffungswert für ein Ersatzfahrzeug gleicher Art und Güte bei einem seriösen Kfz-Händler berücksichtigt Erhaltungs- und Pflegezustand, evtl. Alt- oder Vorschäden, Ausstattung, Laufleistung, Besitzverhältnisse und örtliche Marktlage.',
  },
  {
    id: 'tb_wert_02',
    kategorie: 'Wertermittlung',
    titel: 'Differenzbesteuert',
    text: 'Vergleichbare Fahrzeuge werden überwiegend differenzbesteuert (Händlermarkt) angeboten.',
  },
  // Ergebnis
  {
    id: 'tb_ergebnis_01',
    kategorie: 'Ergebnis',
    titel: 'Totalschaden',
    text: 'Bei dem vorliegenden Schaden handelt es sich um einen wirtschaftlichen Totalschaden, da die Reparaturkosten den Wiederbeschaffungswert übersteigen.',
  },
  {
    id: 'tb_ergebnis_02',
    kategorie: 'Ergebnis',
    titel: 'Reparaturwürdig',
    text: 'Die Reparatur des Fahrzeuges ist technisch möglich und wirtschaftlich sinnvoll.',
  },
  // Schlusswort
  {
    id: 'tb_schluss_01',
    kategorie: 'Schlusswort',
    titel: 'Standard-Schlusswort',
    text: 'Das vorliegende Gutachten wurde nach bestem Wissen und Gewissen erstellt. Für die Richtigkeit der vom Auftraggeber gemachten Angaben wird keine Haftung übernommen.\n\nDas Gutachten dient ausschließlich der Beweissicherung und Feststellung der Schadenhöhe.\n\nLüdenscheid, den {{DATUM}}\n\nAndreas Koop\nKfz-Sachverständiger',
  },
  // Plausibilität
  {
    id: 'tb_plausibel_01',
    kategorie: 'Plausibilität',
    titel: 'Schaden plausibel',
    text: 'Die festgestellten Beschädigungen am Fahrzeug sind mit dem geschilderten Unfallhergang vereinbar und plausibel.',
  },
  // Instandsetzung
  {
    id: 'tb_instandsetzung_01',
    kategorie: 'Instandsetzung',
    titel: 'Fachgerechte Reparatur',
    text: 'Die Instandsetzung des Fahrzeuges sollte in einer qualifizierten Fach- oder Markenvertragswerkstatt erfolgen, um eine fachgerechte Reparatur sicherzustellen.',
  },
];

// ─── Gutachten-Abschnitte ────────────────────────────────────────────────────

interface GutachtenAbschnitt {
  id: string;
  titel: string;
  inhalt: string;
  istPflicht: boolean;
  reihenfolge: number;
}

const STANDARD_ABSCHNITTE: GutachtenAbschnitt[] = [
  { id: 'zusammenfassung', titel: 'Zusammenfassung und Ergebnis', inhalt: '', istPflicht: true, reihenfolge: 1 },
  { id: 'fahrzeug', titel: 'Fahrzeugbeschreibung', inhalt: '', istPflicht: true, reihenfolge: 2 },
  { id: 'auftrag', titel: 'Auftrag', inhalt: '', istPflicht: true, reihenfolge: 3 },
  { id: 'besichtigung', titel: 'Besichtigung', inhalt: '', istPflicht: true, reihenfolge: 4 },
  { id: 'zustand', titel: 'Zustand', inhalt: '', istPflicht: true, reihenfolge: 5 },
  { id: 'lackschicht', titel: 'Lackschichtmessung', inhalt: '', istPflicht: false, reihenfolge: 6 },
  { id: 'schadenhergang', titel: 'Schadenhergang', inhalt: '', istPflicht: true, reihenfolge: 7 },
  { id: 'schadenfeststellung', titel: 'Schadenfeststellung', inhalt: '', istPflicht: true, reihenfolge: 8 },
  { id: 'plausibilitaet', titel: 'Plausibilität', inhalt: '', istPflicht: false, reihenfolge: 9 },
  { id: 'vorschaeden', titel: 'Vorschäden', inhalt: '', istPflicht: true, reihenfolge: 10 },
  { id: 'instandsetzung', titel: 'Instandsetzung', inhalt: '', istPflicht: true, reihenfolge: 11 },
  { id: 'wertermittlung', titel: 'Wertermittlung', inhalt: '', istPflicht: true, reihenfolge: 12 },
  { id: 'ergebnis', titel: 'Ergebnis', inhalt: '', istPflicht: true, reihenfolge: 13 },
  { id: 'schlusswort', titel: 'Schlusswort', inhalt: '', istPflicht: true, reihenfolge: 14 },
];

// ─── Platzhalter ersetzen ────────────────────────────────────────────────────

function ersetzePlatzhalter(text: string, daten: AktenDaten | null): string {
  if (!daten) return text;
  return text
    .replace(/\{\{AKTEN_NR\}\}/g, daten.aktenNr || '')
    .replace(/\{\{AUFTRAGSDATUM\}\}/g, daten.auftragsDatum || '')
    .replace(/\{\{AUFTRAGGEBER\}\}/g, `${daten.halter.anrede} ${daten.halter.vorname} ${daten.halter.nachname}`.trim())
    .replace(/\{\{HALTER\}\}/g, `${daten.halter.anrede} ${daten.halter.vorname} ${daten.halter.nachname}`.trim())
    .replace(/\{\{HALTER_ADRESSE\}\}/g, `${daten.halter.strasse}, ${daten.halter.plz} ${daten.halter.ort}`)
    .replace(/\{\{KENNZEICHEN\}\}/g, daten.fahrzeug.kennzeichen || '')
    .replace(/\{\{FIN\}\}/g, daten.fahrzeug.fin || '')
    .replace(/\{\{HERSTELLER\}\}/g, daten.fahrzeug.hersteller || '')
    .replace(/\{\{MODELL\}\}/g, daten.fahrzeug.modell || '')
    .replace(/\{\{DATUM\}\}/g, new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }))
    .replace(/\{\{BESICHTIGUNGSDATUM\}\}/g, daten.besichtigung.datum || '')
    .replace(/\{\{BESICHTIGUNGSORT\}\}/g, daten.besichtigung.firma || '')
    .replace(/\{\{WBW\}\}/g, daten.bewertung.wiederbeschaffungswert || '')
    .replace(/\{\{RESTWERT\}\}/g, daten.bewertung.restwert || '')
    .replace(/\{\{REPARATURKOSTEN\}\}/g, daten.bewertung.reparaturkosten || '');
}

// ─── Editor Toolbar ──────────────────────────────────────────────────────────

function EditorToolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  const ToolBtn = ({ onClick, active, children, title }: { onClick: () => void; active?: boolean; children: React.ReactNode; title: string }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
        active ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-200 bg-gray-50/80 flex-wrap">
      {/* Text-Formatierung */}
      <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Fett">
        <Bold className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Kursiv">
        <Italic className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Unterstrichen">
        <UnderlineIcon className="w-4 h-4" />
      </ToolBtn>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      {/* Überschriften */}
      <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Überschrift 2">
        <Heading2 className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Überschrift 3">
        <Heading3 className="w-4 h-4" />
      </ToolBtn>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      {/* Ausrichtung */}
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Linksbündig">
        <AlignLeft className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Zentriert">
        <AlignCenter className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Rechtsbündig">
        <AlignRight className="w-4 h-4" />
      </ToolBtn>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      {/* Listen */}
      <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Aufzählung">
        <List className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Nummerierung">
        <ListOrdered className="w-4 h-4" />
      </ToolBtn>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      {/* Tabelle */}
      <ToolBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Tabelle einfügen">
        <TableIcon className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Trennlinie">
        <Minus className="w-4 h-4" />
      </ToolBtn>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      {/* Undo/Redo */}
      <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Rückgängig">
        <Undo className="w-4 h-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Wiederholen">
        <Redo className="w-4 h-4" />
      </ToolBtn>
    </div>
  );
}

// ─── Textbaustein-Panel ──────────────────────────────────────────────────────

function TextbausteinPanel({
  onInsert,
  aktenDaten,
}: {
  onInsert: (text: string) => void;
  aktenDaten: AktenDaten | null;
}) {
  const [suchtext, setSuchtext] = useState('');
  const [offeneKategorie, setOffeneKategorie] = useState<string | null>(null);

  const kategorien = useMemo(() => {
    const cats = new Map<string, Textbaustein[]>();
    STANDARD_TEXTBAUSTEINE.forEach(tb => {
      if (!cats.has(tb.kategorie)) cats.set(tb.kategorie, []);
      cats.get(tb.kategorie)!.push(tb);
    });
    return cats;
  }, []);

  const gefilterteBausteine = useMemo(() => {
    if (!suchtext.trim()) return null;
    const q = suchtext.toLowerCase();
    return STANDARD_TEXTBAUSTEINE.filter(
      tb => tb.titel.toLowerCase().includes(q) || tb.text.toLowerCase().includes(q) || tb.kategorie.toLowerCase().includes(q)
    );
  }, [suchtext]);

  const handleInsert = (tb: Textbaustein) => {
    const text = ersetzePlatzhalter(tb.text, aktenDaten);
    onInsert(text);
    toast.success('Textbaustein eingefügt', { description: tb.titel });
  };

  return (
    <div className="w-full lg:w-72 border-t lg:border-t-0 lg:border-l border-gray-200 bg-white flex flex-col h-full">
      <div className="px-3 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-4 h-4 text-blue-600" />
          <h3 className="text-[13px] font-bold text-gray-900">Textbausteine</h3>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={suchtext}
            onChange={e => setSuchtext(e.target.value)}
            placeholder="Baustein suchen..."
            className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {gefilterteBausteine ? (
          <div className="p-2 space-y-1">
            {gefilterteBausteine.length === 0 && (
              <p className="text-[12px] text-gray-400 text-center py-4">Keine Bausteine gefunden</p>
            )}
            {gefilterteBausteine.map(tb => (
              <button
                key={tb.id}
                onClick={() => handleInsert(tb)}
                className="w-full text-left p-2 rounded-lg hover:bg-blue-50 transition-colors group"
              >
                <div className="text-[11px] font-medium text-blue-600">{tb.kategorie}</div>
                <div className="text-[12px] font-medium text-gray-800">{tb.titel}</div>
                <div className="text-[11px] text-gray-400 line-clamp-2 mt-0.5">{tb.text.substring(0, 80)}...</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="p-2 space-y-0.5">
            {Array.from(kategorien.entries()).map(([kat, bausteine]) => (
              <div key={kat}>
                <button
                  onClick={() => setOffeneKategorie(offeneKategorie === kat ? null : kat)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${offeneKategorie === kat ? 'rotate-90' : ''}`} />
                  <span className="text-[12px] font-semibold text-gray-700">{kat}</span>
                  <span className="text-[10px] text-gray-400 ml-auto">{bausteine.length}</span>
                </button>
                {offeneKategorie === kat && (
                  <div className="ml-4 space-y-0.5 mb-1">
                    {bausteine.map(tb => (
                      <button
                        key={tb.id}
                        onClick={() => handleInsert(tb)}
                        className="w-full text-left p-2 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-[12px] font-medium text-gray-800">{tb.titel}</div>
                        <div className="text-[11px] text-gray-400 line-clamp-1">{tb.text.substring(0, 60)}...</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Abschnitt-Navigation ────────────────────────────────────────────────────

function AbschnittNav({
  abschnitte,
  aktiverAbschnitt,
  onSelect,
}: {
  abschnitte: GutachtenAbschnitt[];
  aktiverAbschnitt: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="w-56 border-r border-gray-200 bg-gray-50/50 flex flex-col h-full">
      <div className="px-3 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-600" />
          <h3 className="text-[13px] font-bold text-gray-900">Abschnitte</h3>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {abschnitte.map((a, i) => (
          <button
            key={a.id}
            onClick={() => onSelect(a.id)}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              aktiverAbschnitt === a.id
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <span className="text-[10px] font-mono text-gray-400 w-4">{i + 1}</span>
            <span className="text-[12px] font-medium flex-1 truncate">{a.titel}</span>
            {a.inhalt.trim() && (
              <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Vorschau-Komponente ─────────────────────────────────────────────────────

function GutachtenVorschau({
  abschnitte,
  aktenDaten,
}: {
  abschnitte: GutachtenAbschnitt[];
  aktenDaten: AktenDaten | null;
}) {
  const d = aktenDaten;
  return (
    <div className="bg-white max-w-full sm:max-w-[800px] mx-auto shadow-lg border border-gray-200 p-4 sm:p-8 min-h-[600px] sm:min-h-[1000px]" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Briefkopf */}
      <div className="flex justify-between items-start mb-6 pb-4 border-b-2 border-[#B91C1C]">
        <div>
          <div className="text-[22px] font-bold text-[#B91C1C] tracking-wide">KOOP</div>
          <div className="text-[9px] text-gray-500 tracking-widest uppercase">Kfz-Gutachten</div>
        </div>
        <div className="text-right text-[11px] text-gray-600 leading-relaxed">
          <div className="font-bold text-gray-900">Kfz Sachverständigenbüro Koop</div>
          <div>Inhaber Andreas Koop</div>
          <div>Kfz-Techniker-Meister & Kfz Sachverständiger</div>
          <div className="mt-1">Mobil: 0170/500 40 22</div>
          <div>Email: info@gutachter-koop.de</div>
        </div>
      </div>

      {/* Akten-Info */}
      {d && (
        <div className="flex justify-between text-[11px] text-gray-500 mb-4">
          <span>AZ: {d.aktenNr} | {d.fahrzeug.kennzeichen}</span>
          <span>{d.auftragsDatum}</span>
        </div>
      )}

      {/* Empfänger */}
      {d && (
        <div className="text-[12px] text-gray-800 mb-6 leading-relaxed">
          <div>{d.halter.anrede}</div>
          <div>{d.halter.vorname} {d.halter.nachname}</div>
          <div>{d.halter.strasse}</div>
          <div>{d.halter.plz} {d.halter.ort}</div>
          <div>{d.halter.land}</div>
        </div>
      )}

      {/* Titel */}
      <h1 className="text-[20px] font-bold text-gray-900 mb-6 text-center">
        {d?.gutachtenTyp || 'Haftpflichtgutachten'}
      </h1>

      {/* Akten-Infotabelle */}
      {d && (
        <table className="w-full text-[11px] mb-6 border-collapse">
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 pr-4 text-gray-500 font-medium w-48">Aktenzeichen</td>
              <td className="py-1.5 text-gray-800">{d.aktenNr}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 pr-4 text-gray-500 font-medium">Auftragsdatum</td>
              <td className="py-1.5 text-gray-800">{d.auftragsDatum}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 pr-4 text-gray-500 font-medium">Auftraggeber / Fahrzeughalter</td>
              <td className="py-1.5 text-gray-800">{d.halter.anrede} {d.halter.vorname} {d.halter.nachname}, {d.halter.strasse}, {d.halter.plz} {d.halter.ort}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 pr-4 text-gray-500 font-medium">Amtl. Kennzeichen</td>
              <td className="py-1.5 text-gray-800 font-mono">{d.fahrzeug.kennzeichen}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 pr-4 text-gray-500 font-medium">Besichtigung</td>
              <td className="py-1.5 text-gray-800">{d.besichtigung.datum} {d.besichtigung.uhrzeit} Uhr, {d.besichtigung.firma}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Abschnitte */}
      {abschnitte.filter(a => a.inhalt.trim()).map(a => (
        <div key={a.id} className="mb-6">
          <h2 className="text-[14px] font-bold text-gray-900 mb-2 border-b border-gray-200 pb-1">{a.titel}</h2>
          <div className="text-[11px] text-gray-700 leading-relaxed whitespace-pre-wrap">{a.inhalt}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Haupt-Komponente ────────────────────────────────────────────────────────

interface GutachtenEditorProps {
  aktenDaten?: AktenDaten | null;
  aktenNr?: string;
  onSave?: (abschnitte: GutachtenAbschnitt[]) => void;
}

export default function GutachtenEditor({ aktenDaten = null, aktenNr, onSave }: GutachtenEditorProps) {
  const storageKey = `gutachten_${aktenNr || 'neu'}`;
  
  // State
  const [abschnitte, setAbschnitte] = useState<GutachtenAbschnitt[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return STANDARD_ABSCHNITTE;
  });
  const [aktiverAbschnitt, setAktiverAbschnitt] = useState(abschnitte[0]?.id || 'zusammenfassung');
  const [modus, setModus] = useState<'editor' | 'vorschau'>('editor');
  const [zeigeTextbausteine, setZeigeTextbausteine] = useState(true);
  const [gespeichert, setGespeichert] = useState(true);

  const aktuellerAbschnitt = abschnitte.find(a => a.id === aktiverAbschnitt);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Text eingeben oder Textbaustein einfügen...' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: aktuellerAbschnitt?.inhalt || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Strip HTML tags for plain text storage (simplified)
      const text = ed.getText();
      setAbschnitte(prev => prev.map(a => a.id === aktiverAbschnitt ? { ...a, inhalt: text } : a));
      setGespeichert(false);
    },
  });

  // Abschnitt wechseln → Editor-Inhalt aktualisieren
  useEffect(() => {
    if (editor && aktuellerAbschnitt) {
      const currentContent = editor.getText();
      if (currentContent !== aktuellerAbschnitt.inhalt) {
        editor.commands.setContent(aktuellerAbschnitt.inhalt || '');
      }
    }
  }, [aktiverAbschnitt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Textbaustein einfügen
  const handleTextbausteinInsert = useCallback((text: string) => {
    if (editor) {
      editor.chain().focus().insertContent(text).run();
    }
  }, [editor]);

  // Speichern
  const handleSave = useCallback(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(abschnitte));
      setGespeichert(true);
      toast.success('Gutachten gespeichert');
      onSave?.(abschnitte);
    } catch {
      toast.error('Fehler beim Speichern');
    }
  }, [abschnitte, storageKey, onSave]);

  // Auto-Befüllung
  const handleAutoBefuellung = useCallback(() => {
    if (!aktenDaten) {
      toast.error('Keine Aktendaten vorhanden', { description: 'Bitte zuerst Fahrzeug- und Halterdaten in der Akte eingeben.' });
      return;
    }
    const neueAbschnitte = abschnitte.map(a => {
      if (a.inhalt.trim()) return a; // Bereits befüllt → nicht überschreiben
      switch (a.id) {
        case 'auftrag':
          return { ...a, inhalt: ersetzePlatzhalter(STANDARD_TEXTBAUSTEINE.find(t => t.id === 'tb_auftrag_01')?.text || '', aktenDaten) };
        case 'besichtigung':
          return { ...a, inhalt: ersetzePlatzhalter(STANDARD_TEXTBAUSTEINE.find(t => t.id === 'tb_besichtigung_01')?.text || '', aktenDaten) + '\n\n' + ersetzePlatzhalter(STANDARD_TEXTBAUSTEINE.find(t => t.id === 'tb_besichtigung_02')?.text || '', aktenDaten) };
        case 'zustand':
          return { ...a, inhalt: ersetzePlatzhalter(STANDARD_TEXTBAUSTEINE.find(t => t.id === 'tb_zustand_01')?.text || '', aktenDaten) };
        case 'vorschaeden':
          return { ...a, inhalt: ersetzePlatzhalter(STANDARD_TEXTBAUSTEINE.find(t => t.id === 'tb_vorschaeden_01')?.text || '', aktenDaten) };
        case 'wertermittlung':
          return { ...a, inhalt: ersetzePlatzhalter(STANDARD_TEXTBAUSTEINE.find(t => t.id === 'tb_wert_01')?.text || '', aktenDaten) };
        case 'schlusswort':
          return { ...a, inhalt: ersetzePlatzhalter(STANDARD_TEXTBAUSTEINE.find(t => t.id === 'tb_schluss_01')?.text || '', aktenDaten) };
        case 'fahrzeug': {
          const f = aktenDaten.fahrzeug;
          return { ...a, inhalt: `Allgemeine Fahrzeugdaten\n\nAmtl. Kennzeichen: ${f.kennzeichen}\nFahrzeug-Identifizierungsnummer: ${f.fin}\nHSN / TSN: ${f.hsnTsn}\nFarbe: ${f.farbe}\nErstzulassung: ${f.erstzulassung}\nLaufleistung: ${f.laufleistung}\nNächste HU: ${f.naechsteHU}\n\nTechnische Fahrzeugdaten\n\nHersteller: ${f.hersteller}\nModell: ${f.modell}\nTyp: ${f.typ}\nBauart: ${f.bauart}\nGetriebe: ${f.getriebe}\nKraftstoff: ${f.kraftstoff}\nLeistung: ${f.leistungKw} kW\nHubraum: ${f.hubraum}\nAntrieb: ${f.antrieb}` };
        }
        case 'zusammenfassung': {
          const b = aktenDaten.bewertung;
          return { ...a, inhalt: `Wiederbeschaffungswert: ${b.wiederbeschaffungswert} EUR\nRestwert: ${b.restwert} EUR\nReparaturkosten: ${b.reparaturkosten} EUR\nWertminderung: ${b.wertminderung} EUR\nNutzungsausfall: ${b.nutzungsausfall} EUR` };
        }
        default:
          return a;
      }
    });
    setAbschnitte(neueAbschnitte);
    setGespeichert(false);
    // Editor-Inhalt aktualisieren
    const neuerAbschnitt = neueAbschnitte.find(a => a.id === aktiverAbschnitt);
    if (editor && neuerAbschnitt) {
      editor.commands.setContent(neuerAbschnitt.inhalt || '');
    }
    toast.success('Gutachten automatisch befüllt', { description: 'Leere Abschnitte wurden mit Aktendaten und Standardtexten befüllt.' });
  }, [aktenDaten, abschnitte, aktiverAbschnitt, editor]);

  return (
    <div className="h-full flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-[#B91C1C]" />
          <div>
            <h2 className="text-[15px] font-bold text-gray-900">Gutachten-Editor</h2>
            <p className="text-[11px] text-gray-400">
              {aktenNr ? `Akte ${aktenNr}` : 'Neues Gutachten'} — {abschnitte.filter(a => a.inhalt.trim()).length}/{abschnitte.length} Abschnitte
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto-Befüllung */}
          <button
            onClick={handleAutoBefuellung}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <Type className="w-3.5 h-3.5" />
            Auto-Befüllung
          </button>
          {/* Textbausteine Toggle */}
          <button
            onClick={() => setZeigeTextbausteine(!zeigeTextbausteine)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
              zeigeTextbausteine ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" />
            Bausteine
          </button>
          {/* Modus-Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setModus('editor')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                modus === 'editor' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              <Edit3 className="w-3.5 h-3.5" />
              Editor
            </button>
            <button
              onClick={() => setModus('vorschau')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                modus === 'vorschau' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              Vorschau
            </button>
          </div>
          {/* Speichern */}
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
              gespeichert
                ? 'bg-gray-100 text-gray-400'
                : 'bg-[#B91C1C] text-white hover:bg-[#991B1B]'
            }`}
          >
            <Save className="w-3.5 h-3.5" />
            {gespeichert ? 'Gespeichert' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Inhalt */}
      <div className="flex-1 flex overflow-hidden">
        {/* Abschnitt-Navigation */}
        <AbschnittNav
          abschnitte={abschnitte}
          aktiverAbschnitt={aktiverAbschnitt}
          onSelect={id => {
            setAktiverAbschnitt(id);
          }}
        />

        {/* Editor oder Vorschau */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {modus === 'editor' ? (
            <>
              <EditorToolbar editor={editor} />
              <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                  <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    {aktuellerAbschnitt?.titel}
                  </div>
                  <EditorContent
                    editor={editor}
                    className="prose prose-sm max-w-none min-h-[400px] [&_.tiptap]:outline-none [&_.tiptap]:min-h-[400px] [&_.tiptap_p]:text-[13px] [&_.tiptap_p]:text-gray-700 [&_.tiptap_p]:leading-relaxed [&_.tiptap_h2]:text-[16px] [&_.tiptap_h2]:font-bold [&_.tiptap_h3]:text-[14px] [&_.tiptap_h3]:font-bold [&_.tiptap_.is-editor-empty:first-child::before]:text-gray-300 [&_.tiptap_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_.is-editor-empty:first-child::before]:float-left [&_.tiptap_.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_table]:border-collapse [&_.tiptap_td]:border [&_.tiptap_td]:border-gray-300 [&_.tiptap_td]:p-2 [&_.tiptap_th]:border [&_.tiptap_th]:border-gray-300 [&_.tiptap_th]:p-2 [&_.tiptap_th]:bg-gray-50"
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
              <GutachtenVorschau abschnitte={abschnitte} aktenDaten={aktenDaten} />
            </div>
          )}
        </div>

        {/* Textbausteine Panel */}
        {modus === 'editor' && zeigeTextbausteine && (
          <TextbausteinPanel onInsert={handleTextbausteinInsert} aktenDaten={aktenDaten} />
        )}
      </div>
    </div>
  );
}
